import defu from 'defu';

export async function usePbApi<T>(url: string, options: any = {}) {
    const auth = useAuthStore();
    const config = useRuntimeConfig();

    const defaults = {
        baseURL: config.public.pb.apiDomain,
        headers: {
            Accept: 'application/json',
        },
    };

    const params = defu(options, defaults);

    let status: number = 200;
    let message: string = '';
    let data: T | null = null;
    let retryAttempted = false;

    async function makeRequest(): Promise<void> {
        // Get a valid token (handles refresh and queueing automatically)
        const token = await auth.getValidTokenForRequest();

        // Set Authorization header if we have a token
        if (token) {
            params.headers.Authorization = token;
        }

        const cfkVid = useCookie('cfk_vid');
        if (cfkVid.value) {
            params.headers['x-pb-cfkvid'] = cfkVid.value;
        }

        const result = await $fetch<T>(url, {
            ...params,
            retry: 0,
            onResponse({ response }) {
                status = response.status;
            },
        });
        data = result as T;
    }

    try {
        await makeRequest();
    } catch (err: any) {
        status = err.response?.status || 500;
        data = (err.response?._data || null) as T;

        // Only attempt refresh once per request to prevent infinite loops
        if (status === 401 && !retryAttempted) {
            retryAttempted = true;
            
            // Attempt to refresh token (this handles queuing internally to prevent race conditions)
            try {
                const refreshed = await auth.refreshAccessToken();
                if (refreshed) {
                    // Refresh succeeded, retry the original request
                    await makeRequest();
                } else {
                    // Refresh failed - auth store has already redirected to login
                    message = 'Session expired. Please log in again.';
                }
            } catch (retryErr: any) {
                // Refresh threw an error - just return the error, no redirect needed
                // (auth store already handles redirects)
                const retryStatus = retryErr.response?.status || 500;
                status = retryStatus;
                
                if (retryErr.response?._data?.errors && Array.isArray(retryErr.response._data.errors)) {
                    message = retryErr.response?._data?.errors[0]?.message || 'An error occurred';
                } else if (retryErr.response?._data?.message) {
                    message = retryErr.response._data.message;
                } else {
                    message = retryErr.message || retryErr.statusText || 'An error occurred';
                }
            }
        } else {
            // Non-401 error or already retried - return error
            if (err.response?._data?.errors && Array.isArray(err.response._data.errors)) {
                message = err.response?._data?.errors[0]?.message || 'An error occurred';
            } else if (err.response?._data?.message) {
                message = err.response._data.message;
            } else {
                message = err.message || err.statusText || 'An error occurred';
            }
        }
    }

    return { data: data as T, status, message };
}
